import {
  CalendarDays,
  CheckCircle2,
  Clock,
  FileText,
  Headset,
  PenLine,
  Plug,
  SendHorizonal,
  Wrench,
  type LucideIcon,
} from "lucide-react";

const STEP_ICONS: LucideIcon[] = [
  FileText,
  SendHorizonal,
  CheckCircle2,
  PenLine,
  Clock,
  CalendarDays,
  Wrench,
  Plug,
  Headset,
];

type JourneyStep = {
  readonly title: string;
  readonly body: string;
};

type JourneyTimelineProps = {
  steps: readonly JourneyStep[];
};

/**
 * Shared numbered journey timeline used on the finance pages. Each step shows an
 * icon-in-circle marker connected by a vertical line so the nine steps read as one
 * continuous flow rather than nine unrelated cards.
 */
export function JourneyTimeline({ steps }: JourneyTimelineProps) {
  return (
    <ol className="relative max-w-3xl space-y-4">
      {steps.map((step, i) => {
        const Icon = STEP_ICONS[i % STEP_ICONS.length];
        const isLast = i === steps.length - 1;
        return (
          <li key={step.title} className="relative pl-14 md:pl-16">
            {/* connector */}
            {!isLast && (
              <span
                aria-hidden="true"
                className="absolute left-[19px] md:left-[23px] top-10 bottom-[-1rem] w-px bg-border"
              />
            )}
            <span className="absolute left-0 top-0 inline-flex h-10 w-10 md:h-12 md:w-12 items-center justify-center rounded-full bg-accent text-accent-foreground shadow-soft">
              <Icon className="h-4 w-4 md:h-5 md:w-5" aria-hidden="true" />
              <span className="absolute -bottom-1 -right-1 inline-flex h-5 w-5 items-center justify-center rounded-full border border-border bg-background text-[10px] font-bold text-foreground">
                {i + 1}
              </span>
            </span>
            <div className="rounded-2xl border border-border bg-background p-5 md:p-6 shadow-soft">
              <h3 className="text-base md:text-lg font-bold text-foreground mb-1.5">
                {step.title}
              </h3>
              <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                {step.body}
              </p>
            </div>
          </li>
        );
      })}
    </ol>
  );
}
